import React, { useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, useMediaQuery, useTheme
} from '@mui/material';
import { TrendingUp, Receipt, SendToMobile, AssignmentReturn, CheckCircle } from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import StatusChip from '../common/StatusChip';
import { MobileCard, MobileRow } from '../common/MobileCard';

const StatCard = ({ icon, title, value, subtitle, color, bg }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 11, md: 12 } }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color, mt: 0.5, fontSize: { xs: '1.4rem', md: '1.75rem' } }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: bg, ml: 1, flexShrink: 0 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: { xs: 22, md: 28 } } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PIE_COLORS = ['#1a237e', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1'];

export default function Dashboard() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/dashboard/summary')
      .then(res => setData(res.data.data))
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const { invoiceStats, remittanceStats, boeStats, recentRemittances, monthlyData, pendingBOE } = data || {};

  const invoicePieData = [
    { name: 'Draft',     value: invoiceStats?.draft     || 0 },
    { name: 'Pending',   value: invoiceStats?.pending   || 0 },
    { name: 'Submitted', value: invoiceStats?.submitted || 0 },
    { name: 'Paid',      value: invoiceStats?.paid      || 0 },
  ].filter(d => d.value > 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
          Welcome back, {user?.name?.split(' ')[0]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 11, md: 13 } }}>
          Identium FX · {new Date().toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
        </Typography>
      </Box>

      {/* KPI Cards — 2×2 on mobile, 4×1 on desktop */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={6} lg={3}>
          <StatCard icon={<Receipt />} title="Invoices (This Year)"
            value={invoiceStats?.total_invoices || 0}
            subtitle={`${invoiceStats?.pending || 0} pending`}
            color="#1a237e" bg="#e8eaf6" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <StatCard icon={<SendToMobile />} title="Remittances"
            value={`$${parseFloat(remittanceStats?.total_amount_usd || 0).toLocaleString('en-US', { notation: isMobile ? 'compact' : 'standard', maximumFractionDigits: 0 })}`}
            subtitle={`${remittanceStats?.total_remittances || 0} txns`}
            color="#2e7d32" bg="#e8f5e9" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <StatCard icon={<AssignmentReturn />} title="BOE Pending"
            value={boeStats?.pending_knockoff || 0}
            subtitle={`${boeStats?.knocked_off || 0} knocked off`}
            color={boeStats?.pending_knockoff > 0 ? '#ed6c02' : '#2e7d32'}
            bg={boeStats?.pending_knockoff > 0 ? '#fff3e0' : '#e8f5e9'} />
        </Grid>
        <Grid item xs={6} lg={3}>
          <StatCard icon={<TrendingUp />} title="Outstanding"
            value={`$${parseFloat(invoiceStats?.outstanding_usd || 0).toLocaleString('en-US', { notation: isMobile ? 'compact' : 'standard', maximumFractionDigits: 0 })}`}
            subtitle="Unpaid invoices"
            color="#d32f2f" bg="#ffebee" />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={12} md={invoicePieData.length > 0 ? 8 : 12}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: 13, md: 15 } }}>
                Monthly Remittances (Last 6 Months)
              </Typography>
              {(monthlyData || []).length === 0 ? (
                <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No data yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
                  <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => [`$${v.toLocaleString()}`, 'Amount']} />
                    <Bar dataKey="amount" fill="#1a237e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {invoicePieData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: 13, md: 15 } }}>
                  Invoice Status
                </Typography>
                <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
                  <PieChart>
                    <Pie data={invoicePieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {invoicePieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Bottom tables */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
        {/* Recent Remittances */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontSize: { xs: 13, md: 15 } }}>Recent Remittances</Typography>
              </Box>
              {isMobile ? (
                <Box sx={{ px: 1.5, pb: 1 }}>
                  {(recentRemittances || []).length === 0 ? (
                    <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>No remittances yet</Typography>
                  ) : recentRemittances.map(r => (
                    <MobileCard key={r.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.remittance_number}</Typography>
                        <StatusChip status={r.status} />
                      </Box>
                      <MobileRow label="Beneficiary" value={r.beneficiary_name} />
                      <MobileRow label="Amount" value={`$${parseFloat(r.amount).toLocaleString()}`} bold />
                    </MobileCard>
                  ))}
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Beneficiary</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(recentRemittances || []).length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No remittances yet</TableCell></TableRow>
                    ) : recentRemittances.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontSize: 12 }}>{r.remittance_number}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.beneficiary_name}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>${parseFloat(r.amount).toLocaleString()}</TableCell>
                        <TableCell><StatusChip status={r.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending BOE */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontSize: { xs: 13, md: 15 } }}>Pending BOE Knock-off</Typography>
                {(boeStats?.pending_knockoff || 0) > 0 && (
                  <Chip label={boeStats.pending_knockoff} size="small" color="warning" />
                )}
              </Box>
              {isMobile ? (
                <Box sx={{ px: 1.5, pb: 1 }}>
                  {(pendingBOE || []).length === 0 ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 22, mb: 0.5 }} />
                      <Typography color="text.secondary" variant="body2">All BOEs knocked off</Typography>
                    </Box>
                  ) : pendingBOE.map(b => (
                    <MobileCard key={b.id}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 12, mb: 0.5 }}>{b.boe_number}</Typography>
                      <MobileRow label="Date" value={b.boe_date} />
                      <MobileRow label="Value" value={`$${parseFloat(b.invoice_value || 0).toLocaleString()}`} bold />
                    </MobileCard>
                  ))}
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>BOE No.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(pendingBOE || []).length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        <CheckCircle sx={{ color: 'success.main', fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                        All BOEs knocked off
                      </TableCell></TableRow>
                    ) : pendingBOE.map(b => (
                      <TableRow key={b.id} hover>
                        <TableCell sx={{ fontSize: 12 }}>{b.boe_number}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.boe_date}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>${parseFloat(b.invoice_value || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
